import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Input, Result, Row, Select, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateCurrencyLookupListFormValues } from '../../../../../interfaces/lookup/currency';
import { IUpdateCurrency } from '../../../../../interfaces/lookup/currency/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchBankAccountsLookupList, fetchCurrencyById, updateCurrency } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const CurrencyLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const { bankAccounts } = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateCurrencyLookupListFormValues) => {
        const body: IUpdateCurrency = {
            id: lookupSlice.detailsPage.currency.data?.id,
            conversionRateDate: values.conversionRateDate?.format('YYYY-MM-DDTHH:mm:ss'),
            ...(values as any),
        };

        dispatch(updateCurrency(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.currency.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/currency', { replace: true });
        }
    }, [lookupSlice.detailsPage.currency.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchCurrencyById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.currency.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.currency.data.id,
                code: lookupSlice.detailsPage.currency.data.code,
                description: lookupSlice.detailsPage.currency.data.description,
                defaultBankAccountId: lookupSlice.detailsPage.currency.data.defaultBankAccount?.id,
                conversionRate: lookupSlice.detailsPage.currency.data.conversionRate,
                conversionRateDate: lookupSlice.detailsPage.currency.data.conversionRateDate
                    ? dayjs(lookupSlice.detailsPage.currency.data.conversionRateDate)
                    : undefined,
            } as IUpdateCurrencyLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.currency.data]);

    useEffect(() => {
        dispatch(fetchBankAccountsLookupList());
    }, []);

    if (lookupSlice.detailsPage.currency.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="currency-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage currency lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Currency',
                        path: '/admin/lookup-lists/currency',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.currency.data?.description || '-',
                        path: `/admin/lookup-lists/currency/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/currency/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.currency.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.currency.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.currency.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.currency.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.currency.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.currency.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.currency.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.currency.fetchStatus} about={'Currency'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Currency Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <Form.Item name="code" rules={[{ required: true, message: "'Code' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Bank account</label>
                                    <Form.Item name="defaultBankAccountId">
                                        <Select
                                            showSearch
                                            placeholder="Select a bank account"
                                            filterOption={(input, option) =>
                                                (option!.children as unknown as string).toLowerCase().startsWith(input.toLowerCase())
                                            }
                                        >
                                            {bankAccounts.lookup.data.map((bankAccount) => (
                                                <Select.Option
                                                    key={bankAccount.key}
                                                    value={bankAccount.key}
                                                    disabled={bankAccount.isDeleted}
                                                >
                                                    {bankAccount.value}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Conversion rate</label>
                                    <Form.Item name="conversionRate" rules={[{ required: true, message: "'Conversion rate' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Conversion date</label>
                                    <Form.Item
                                        name="conversionRateDate"
                                        rules={[{ required: true, message: "'Conversion date' is required" }]}
                                    >
                                        <DatePicker />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.currency?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.currency.data?.created
                                            ? dayjs(lookupSlice.detailsPage.currency.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.currency.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.currency.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.currency.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
